import api from './api';

export interface LoginRequest {
  email: string;
  password: string;
  user_type?: number;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  centerName?: string;
  phone?: string;
  role_id: 2 | 3 | 4; // 2 = training_center, 3 = trainee, 4 = consultant
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  userId?: string;
  email?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: {
    id: string;
    email: string;
    role_id: number;
    role?: {
      id: number;
      name: string;
    } | null;
    firstName?: string;
    lastName?: string;
    centerName?: string;
    phone?: string;
    isEmailVerified: boolean;
  };
}

export interface VerifyEmailRequest {
  email: string;
  code: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: {
    id: string;
    email: string;
    role_id: number;
    role?: {
      id: number;
      name: string;
    } | null;
    firstName?: string;
    lastName?: string;
    centerName?: string;
    phone?: string;
    isEmailVerified: boolean;
  };
}

export interface ResendVerificationRequest {
  email: string;
  user_type?: number;
}

export interface ResendVerificationResponse {
  success: boolean;
  message: string;
  email?: string;
  expiresIn?: string;
}

class AuthService {
  private readonly baseUrl = '/auth';

  async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      // Clear any existing auth data before login (without making API call)
      this.clearAuthData();

      const response = await api.post(`${this.baseUrl}/login`, data);

      // Validate response structure — token is in httpOnly cookie, not response body
      if (response.data && response.data.success && response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));

        return response.data;
      } else {
        throw new Error('Invalid login response from server');
      }
    } catch (error: any) {
      // Clear any partial data on error
      this.clearAuthData();

      // For email verification errors, preserve the full error response
      // NestJS wraps errors differently - data can be an object or just the message string
      const responseData = error.response?.data;
      let errorData: any = {};
      let errorMessage = 'Erreur de connexion';
      let errorCode: string | undefined;

      // Handle different NestJS error formats
      if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (responseData?.message) {
        errorMessage = responseData.message;
        errorData = responseData;
      } else if (responseData) {
        errorData = responseData;
        errorMessage = responseData.message || error.message || 'Erreur de connexion';
      } else {
        errorMessage = error.message || 'Erreur de connexion';
      }

      errorCode = errorData?.code || responseData?.code;

      // Check if this is an email verification error
      const isEmailVerificationError =
        errorCode === 'EMAIL_NOT_VERIFIED' ||
        errorMessage?.includes('Veuillez vérifier votre email') ||
        errorMessage?.includes('verify your email');

      if (isEmailVerificationError) {
        // Create a custom error with all the email verification data
        const emailVerificationError = new Error(errorMessage);
        (emailVerificationError as any).response = {
          ...error.response,
          data: {
            code: 'EMAIL_NOT_VERIFIED',
            message: errorMessage,
            email: errorData?.email || responseData?.email,
            user_type: errorData?.user_type || responseData?.user_type,
            canResend: errorData?.canResend !== undefined ? errorData.canResend : (responseData?.canResend !== undefined ? responseData.canResend : true)
          }
        };
        throw emailVerificationError;
      }

      throw new Error(errorMessage);
    }
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    try {


      const response = await api.post(`${this.baseUrl}/register`, data);

      return response.data;
    } catch (error: any) {


      // Enhanced mobile-specific error handling
      if (error.code === 'ECONNABORTED') {
        throw new Error('Timeout de connexion. Vérifiez votre connexion internet.');
      }

      if (!error.response) {
        throw new Error('Erreur de connexion. Vérifiez votre connexion internet.');
      }

      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Données invalides. Vérifiez vos informations.');
      }

      if (error.response?.status === 409) {
        throw new Error('Cet email existe déjà. Utilisez un autre email ou connectez-vous.');
      }

      if (error.response?.status >= 500) {
        throw new Error('Erreur serveur. Veuillez réessayer plus tard.');
      }

      throw new Error(error.response?.data?.message || 'Erreur d\'inscription');
    }
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/verify-email`, data);

      // Token is in httpOnly cookie; store user data only
      if (response.data && response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur de vérification');
    }
  }

  async resendVerification(data: ResendVerificationRequest): Promise<ResendVerificationResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/resend-verification`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur d\'envoi du code');
    }
  }

  async logout(): Promise<void> {
    try {
      const url = `${this.baseUrl}/logout`;
      await api.post(url);
    } catch (error) {

    } finally {
      localStorage.removeItem('user');
    }
  }

  async refreshToken(): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/refresh-token`);
      // Cookie is refreshed by the backend; nothing to store client-side
    } catch (error) {
      this.logout();
    }
  }

  getToken(): null {
    // Token is in an httpOnly cookie — not accessible from JS
    return null;
  }

  getUser(): any {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    const user = this.getUser();
    return !!user;
  }

  isEmailVerified(): boolean {
    const user = this.getUser();
    return user?.isEmailVerified || false;
  }

  getRole(): string | null {
    const user = this.getUser();
    return user?.role?.name || null;
  }

  getRoleId(): number | null {
    const user = this.getUser();
    return user?.role_id || null;
  }

  async getTrainingCenterInfo(): Promise<any> {
    try {
      const response = await api.get(`${this.baseUrl}/training-center`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération du centre de formation');
    }
  }

  async updateTrainingCenter(data: any): Promise<any> {
    try {
      // Check if data contains a file (logo)
      if (data.logo && data.logo instanceof File) {
        // Create FormData for file upload
        const formData = new FormData();

        // Add all non-file fields
        Object.keys(data).forEach(key => {
          if (key !== 'logo' && data[key] !== undefined) {
            if (key === 'field_ids' && Array.isArray(data[key])) {
              // Handle array fields
              data[key].forEach((item: any, index: number) => {
                formData.append(`${key}[${index}]`, item);
              });
            } else {
              // Convert null to empty string for FormData (to allow clearing fields)
              const value = data[key] === null ? '' : data[key];
              formData.append(key, value);
            }
          }
        });

        // Add logo file
        formData.append('logo', data.logo);

        const response = await api.put(`${this.baseUrl}/training-center`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } else {
        // Regular JSON request for non-file updates
        const response = await api.put(`${this.baseUrl}/training-center`, data);
        return response.data;
      }
    } catch (error: any) {
      // Handle specific HTTP status codes
      if (error.response?.status === 413) {
        throw new Error('File too large');
      }
      if (error.response?.status === 415) {
        throw new Error('Invalid file format');
      }
      throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du centre de formation');
    }
  }

  clearAuthData(): void {
    localStorage.removeItem('user');
  }

  validateAuthState(): boolean {
    const user = this.getUser();
    if (!user) return false;
    try {
      return !!(user.id && user.email && user.role_id);
    } catch {
      return false;
    }
  }

  // Delete consultant account permanently
  async deleteConsultantAccount(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete('/consultants/account');
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async forgotPassword(email: string) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, password: string) {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  }
}

export const authService = new AuthService(); 